import { useEffect, useState } from "react";
import { api, formatZAR, formatInt, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Kpi";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const PERIODS = [
    { label: "30d", days: 30 },
    { label: "90d", days: 90 },
    { label: "365d", days: 365 },
    { label: "All", days: 3650 },
];

function formatKg(v) {
    if (v == null || isNaN(v) || v === 0) return "—";
    return `${Number(v).toLocaleString("en-ZA", { maximumFractionDigits: 1 })} kg`;
}

export default function Profitability() {
    const [days, setDays] = useState(90);
    const [data, setData] = useState({ items: [], missing_cogs: [], missing_kg: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/profitability?days=${days}`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [days]);

    return (
        <div>
            <PageHeader title="Profitability" subtitle="Revenue, fees, COGS, kg and net profit per product">
                <div className="inline-flex border border-[color:var(--bl-border)] rounded-sm bg-[color:var(--bl-surface)]">
                    {PERIODS.map((p) => (
                        <button key={p.days} data-testid={`profit-period-${p.label}`} onClick={() => setDays(p.days)}
                            className={`px-3 py-1.5 text-xs uppercase tracking-wider ${days === p.days ? "bg-[color:var(--bl-takealot-blue)] text-white" : "text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)]"}`}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </PageHeader>

            {(data.missing_cogs?.length > 0 || data.missing_kg?.length > 0) && (
                <div data-testid="missing-data-banner" className="mb-4 flex items-start gap-3 border border-[color:var(--bl-warning)]/40 bg-[color:var(--bl-warning)]/10 p-3 rounded-sm text-sm">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-[color:var(--bl-warning)]" />
                    <div className="text-[color:var(--bl-text)] text-xs leading-relaxed">
                        {data.missing_cogs?.length > 0 && <div>{data.missing_cogs.length} products missing <span className="font-semibold">cost</span> — net profit will be incomplete.</div>}
                        {data.missing_kg?.length > 0 && <div>{data.missing_kg.length} products missing <span className="font-semibold">kg/unit</span> — kg totals will be off.</div>}
                        <div className="text-[color:var(--bl-text-dim)] mt-1">Fix on the <Link to="/products" className="text-[color:var(--bl-takealot-orange)] hover:underline">Products</Link> page.</div>
                    </div>
                </div>
            )}

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm overflow-x-auto shadow-sm">
                <table className="w-full text-sm min-w-[1100px]">
                    <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)] bg-[color:var(--bl-bg-2)]/40">
                        <tr>
                            <th className="text-left px-4 py-2">Product</th>
                            <th className="text-right px-3 py-2">Units</th>
                            <th className="text-right px-3 py-2">Kg</th>
                            <th className="text-right px-3 py-2">Revenue</th>
                            <th className="text-right px-3 py-2">Fees</th>
                            <th className="text-right px-3 py-2">COGS</th>
                            <th className="text-right px-3 py-2">Net</th>
                            <th className="text-right px-3 py-2">R / kg</th>
                            <th className="text-right px-3 py-2">Margin %</th>
                        </tr>
                    </thead>
                    <tbody data-testid="profit-table">
                        {loading && (<tr><td colSpan={9} className="text-center text-[color:var(--bl-text-dim)] py-6">Crunching numbers…</td></tr>)}
                        {!loading && data.items.length === 0 && (<tr><td colSpan={9} className="text-center text-[color:var(--bl-text-dim)] py-6">No sales in this period.</td></tr>)}
                        {data.items.map((r) => {
                            const margin = r.margin_pct ?? 0;
                            const marginColor = margin > 25 ? "text-[color:var(--bl-success)]" : margin > 10 ? "text-[color:var(--bl-text)]" : margin > 0 ? "text-[color:var(--bl-warning)]" : "text-[color:var(--bl-danger)]";
                            return (
                                <tr key={r.sku} data-testid={`profit-row-${r.sku}`} className="border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/50">
                                    <td className="px-4 py-2">
                                        <Link to={`/products/${encodeURIComponent(r.sku)}`} className="flex items-center gap-2 hover:opacity-80">
                                            {r.image_url && <img src={r.image_url} alt="" className="w-8 h-8 object-cover rounded-sm border border-[color:var(--bl-border)] bg-white" />}
                                            <div>
                                                <div className="line-clamp-1 text-[color:var(--bl-text)]">{r.title || r.sku}</div>
                                                <div className="font-mono-num text-[10px] text-[color:var(--bl-text-dim)]">{r.sku}</div>
                                            </div>
                                        </Link>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-num">{formatInt(r.units)}</td>
                                    <td className="px-3 py-2 text-right font-mono-num text-[color:var(--bl-accent-coffee)]">{formatKg(r.kg)}</td>
                                    <td className="px-3 py-2 text-right font-mono-num">{formatZAR(r.revenue)}</td>
                                    <td className="px-3 py-2 text-right font-mono-num text-[color:var(--bl-takealot-orange)]">{formatZAR(r.fees)}</td>
                                    <td className="px-3 py-2 text-right font-mono-num text-[color:var(--bl-text-dim)]">{r.cost == null ? "—" : formatZAR(r.cogs_total)}</td>
                                    <td className={`px-3 py-2 text-right font-mono-num ${r.net >= 0 ? "text-[color:var(--bl-success)]" : "text-[color:var(--bl-danger)]"}`}>{formatZAR(r.net)}</td>
                                    <td className="px-3 py-2 text-right font-mono-num">{r.avg_net_per_kg != null ? formatZAR(r.avg_net_per_kg) : "—"}</td>
                                    <td className={`px-3 py-2 text-right font-mono-num ${marginColor}`}>{r.margin_pct == null ? "—" : `${r.margin_pct.toFixed(1)}%`}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
